import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Prop, Ref, SimpleEvent, Watch } from '@eastsideco/salvo-ts';
import { cart } from '@/theme/Cart';
import { setVariantUrlParam } from '@/utils/products';
import { formatMoney } from '@/utils/formatting';
import { QuantitySelector } from '@/components/QuantitySelector/QuantitySelector';
import { ProductOptionPicker } from './ProductOptionPicker';

@Component
export class ProductForm extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop defaultVariant!: Shopify.Liquid.Variant;
    @Prop({ default: null }) scrollTargetSelector!: string|null;
    @Prop({ default: false }) disableDefaultVariant!: boolean;
    @Ref({ required: false }) options!: ProductOptionPicker[];
    @Ref() quantitySelector!: QuantitySelector;
    @Ref({ required: false }) productPrice!: HTMLElement[];
    @Ref({ required: false }) productSalePrice!: HTMLElement[];
    @Ref({ required: false }) productSaleInfo!: HTMLElement[];
    @Ref({ required: false }) productSaleSavings!: HTMLElement[];
    @Ref() submitButton!: HTMLButtonElement[];
    @Ref form!: HTMLFormElement;
    @Ref error!: HTMLElement;
    @Ref errorMessage!: HTMLElement;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _selectedVariant: Shopify.Liquid.Variant | null = null;
    protected get selectedVariant() {
        return this._selectedVariant;
    }
    protected set selectedVariant(v) {
        this._selectedVariant = v;
        this.handleVariantChanged();
    }
    protected quantity: number = 1;

    init() {

        // Set default variant
        this.selectedVariant = this.defaultVariant;

        // Listen for quantity change
        this.listenForQuantityChange();

        // Listen for variant change
        this.listenForVariantChange();

        // Listen for form submission
        this.listenForFormSubmission();

        // Update add to cart price
        this.updateProductPrice();


        // Check product is active
        if (this.product.published_at == null) {
            // Disable
            this.updateActionsVisibility(true);
            return;
        }

    }

    protected handleVariantChanged() {
        setVariantUrlParam(this.selectedVariant?.id || null);
    }

    private _onSelectedPropertyChange = new SimpleEvent<string>();
    public get onSelectedPropertyChange() {
        return this._onSelectedPropertyChange.asEvent();
    }

    private checkVariantAvailability() {
        // Check variant is defined
        if (this.selectedVariant === null) {

            // Disable
            this.updateActionsVisibility(true);
            return;
        }

        // Check variant is in stock
        if (!this.selectedVariant.available) {

            // Disable
            this.updateActionsVisibility(true);
            return;
        }

        // Enable
        this.updateActionsVisibility(false);
    }

    private listenForQuantityChange() {
        this.$subscribe(this.quantitySelector.onChange, (quantity: number) => {

            // Hide error
            this.error.classList.remove('is-active');

            // Update quantity
            this.quantity = quantity;

            // Update add to cart price
            this.updateProductPrice();

        });
    }

    private listenForVariantChange() {
        // eslint-disable-next-line ban/ban
        this.options.forEach( (option) => {
            if (option) {

                if (!this.disableDefaultVariant) {
                    option.renderOptions();
                }

                // Listen for variant change
                this.$subscribe(option.onSelectedVariantChange, (variant) => {

                    // Set selected variant value
                    if (variant === null) {
                        return;
                    }
                    this.selectedVariant = variant;

                    // Check variant availability
                    this.checkVariantAvailability();

                    // Update add to cart price
                    this.updateProductPrice();

                    // Update submit buttons
                    for (const submitButton of this.submitButton) {
                        submitButton.classList.remove('product-form__actions__submit--disabled');
                        submitButton.disabled = false;
                    }

                    // Hide error
                    this.error.classList.remove('is-active');
                });

                // Set inital selected variant value
                this.selectedVariant = option.selectedVariant;

            }
        });
    }

    private listenForFormSubmission() {
        for (const submitButton of this.submitButton) {
            this.$listen(submitButton, 'click', () => {
                this.addToCart().catch((e) => {
                    this.$log.error(`Add to cart failed`, { e });
                });
            });
        }
    }

    private updateActionsVisibility(disabled: boolean) {
        // Disable button
        if (disabled) {
            for (const submitButton of this.submitButton) {
                submitButton.classList.add('product-form__actions__submit--unavailable');
            }

        // Enable button
        } else {
            for (const submitButton of this.submitButton) {
                submitButton.classList.remove('product-form__actions__submit--unavailable');
            }
        }
    }

    private updateProductPrice() {
        // Ensure variant is selected
        if (this.selectedVariant) {

            // Get price of variant * quantity
            const price = this.selectedVariant.price * this.quantity;
            const formattedPrice = formatMoney(price);
            if (typeof formattedPrice === 'undefined') {
                return;
            }

            // Update price
            for (const productPrice of this.productPrice) {
                productPrice.textContent = formattedPrice;
            }

            let salePrice = 0;

            // Get sale price of variant * quantity
            if (this.selectedVariant.compare_at_price) {
                salePrice = this.selectedVariant.compare_at_price * this.quantity;
            }

            const formattedSalePrice = formatMoney(salePrice);
            if (typeof salePrice === 'undefined') {
                return;
            }

            // Update sale price
            for (const salePrice of this.productSalePrice) {
                if (salePrice) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    salePrice.textContent = formattedSalePrice!;
                }
            }

            // Check sale price
            if (this.selectedVariant.compare_at_price === null) {
                // Hide sale price
                for (const productSalePrice of this.productSalePrice) {
                    productSalePrice.classList.add('visually-hidden');
                }

                for (const productPrice of this.productPrice) {
                    productPrice.classList.remove('product-form__price--sale');
                }

                for (const productSaleInfo of this.productSaleInfo) {
                    productSaleInfo.classList.add('visually-hidden');
                }

                for (const savingsMessage of this.productSaleSavings) {
                    savingsMessage.classList.add('visually-hidden');
                }

            // Update sale price
            } else {
                const salePrice = this.selectedVariant.compare_at_price * this.quantity;
                const formattedSalePrice = formatMoney(salePrice);
                if (typeof formattedSalePrice === 'undefined') {
                    return;
                }
                for (const productSalePrice of this.productSalePrice) {
                    productSalePrice.classList.remove('visually-hidden');
                }

                for (const productSaleInfo of this.productSaleInfo) {
                    productSaleInfo.classList.remove('visually-hidden');
                }

                for (const productPrice of this.productPrice) {
                    productPrice.classList.add('product-form__price--sale');
                }

                //Update Sale Savings Message
                if (this.productSaleSavings) {
                    // eslint-disable-next-line max-len
                    const salePercent = ((this.selectedVariant.price * this.quantity) / (this.selectedVariant.compare_at_price * this.quantity)) * 100 ;
                    const salePercentOff = (100 - salePercent).toFixed(0);
                    // eslint-disable-next-line max-len
                    const saleValue = (this.selectedVariant.compare_at_price * this.quantity) - (this.selectedVariant.price * this.quantity);

                    const savingString = 'You Save ' + salePercentOff + '% (' + formatMoney(saleValue) + ')';

                    for (const savingsMessage of this.productSaleSavings) {
                        savingsMessage.textContent = savingString;
                        savingsMessage.classList.remove('visually-hidden');
                    }

                }
            }
        }
    }

    protected async addToCart() {
        const properties = this.getFormData();

        // Attempt add to cart
        try {

            // Check variant exists
            const variant = this.selectedVariant;
            if (!variant) {
                this.showError(`Please select an option.`);
                return;
            }

            // Add to cart
            await cart.asInteraction(async() => {
                await cart.addItem({
                    id: variant.id,
                    quantity: this.quantity,
                    properties: properties
                });
            });

        // Handle errors
        } catch (event) {

            // Log and show FE error
            this.$log.error('Cart error', { event });
            const { message } = event as Error;
            this.showError(message);
        }

        window.location.href ='/cart';
    }

    protected getFormData() {
        const formData = new FormData(this.form);
        const properties: {[key: string]: string} = {};
        for (const [key, value] of formData.entries()) {
            if (key.indexOf('properties') > -1) {
                const keyString = (key as string).replace('properties[', '').replace(']', '');
                properties[keyString] = value as string;
            }
        }

        return properties;
    }

    protected showError(msg: string | null): void {
        this.errorMessage.textContent = msg;
        if (msg) {
            this.error.classList.add('is-active');
        } else {
            this.error.classList.remove('is-active');
        }
    }

}
